import { type FC, useState, useEffect, useCallback, type ReactNode } from 'react';
import classNames from 'classnames';
import Cropper from 'react-easy-crop';
import {
    getZoomRange,
    getZoomCoordinatesFromUrl,
    getRotationFromUrl,
    reformatCropArea,
} from './utils';
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalSpinner } from 'dibs-elements/exports/ModalSpinner';
import { Banner } from 'dibs-elements/exports/Banner';
import { Header } from 'dibs-elements/exports/Header';
import { Slider } from 'dibs-elements/exports/Slider';
import { Link } from 'dibs-elements/exports/Link';
import ExclamationCircle from 'dibs-icons/exports/legacy/ExclamationCircle';
import ArrowClockwise from 'dibs-icons/exports/legacy/ArrowClockwise';
import styles from './styles/ImageEditModal.scss';

type ImageEditModalProps = {
    isOpen: boolean;
    changesRequired: boolean;
    onClose: () => void;
    onSave: (editDetails: {
        crop: {
            x1: number;
            x2: number;
            y1: number;
            y2: number;
        };
        degreesRotation: number;
        resize: {
            newHeight: number;
            newWidth: number;
        };
    }) => void;
    onDelete: () => void;
    imageUrl: string;
    imageReqs: {
        aspect: number;
        cropShape: 'round' | 'rect';
        targetWidth: number;
        targetHeight: number;
    };
    customStyles?: string;
    title?: string | ReactNode;
    subTitle?: string | ReactNode;
    saveCtaText?: string | ReactNode;
    enableImageRotation: boolean;
    showOnboardingCopy?: boolean;
    showConfirmationBeforeClose?: boolean;
};

const initialCropArea = { width: 0, height: 0, x: 0, y: 0 };

export const ImageEditModal: FC<ImageEditModalProps> = props => {
    const {
        isOpen,
        onClose,
        onSave,
        imageUrl,
        imageReqs,
        title,
        subTitle,
        saveCtaText,
        customStyles,
        changesRequired,
        onDelete,
        enableImageRotation,
        showOnboardingCopy,
        showConfirmationBeforeClose,
    } = props;

    const [initialCroppedAreaPixels, setInitialCroppedAreaPixels] = useState(initialCropArea);
    const [rotationFactor, setRotationFactor] = useState(180);
    const [maxZoom, setMaxZoom] = useState(1);

    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [rotation, setRotation] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [exitAttempt, setExitAttempt] = useState(false);
    const [cropArea, setCropArea] = useState(initialCropArea);
    const [isSaving, setIsSaving] = useState(false);
    const [loadedMediaSize, setLoadedMediaSize] = useState({
        width: 0,
        height: 0,
        naturalWidth: 0,
        naturalHeight: 0,
    });

    useEffect(() => {
        if (isOpen) {
            setExitAttempt(false);
            setIsSaving(false);
            setRotation(getRotationFromUrl(imageUrl));
            setInitialCroppedAreaPixels(getZoomCoordinatesFromUrl(imageUrl));
        }
    }, [isOpen, imageUrl]);

    useEffect(() => {
        setRotation(rotation % 360);
    }, [rotation]);

    const onCropComplete = useCallback(
        (croppedArea: unknown, croppedAreaPixels: typeof initialCropArea) => {
            setCropArea(croppedAreaPixels);
        },
        []
    );

    async function onSaveAndClose(): Promise<void> {
        setIsSaving(true);
        const reformattedCropArea = reformatCropArea(cropArea, loadedMediaSize, rotation);
        await onSave({
            crop: reformattedCropArea,
            degreesRotation: rotation,
            resize: {
                newHeight: imageReqs.targetHeight,
                newWidth: imageReqs.targetWidth,
            },
        });
        setIsSaving(false);
        onClose();
    }

    const parentImage = imageUrl?.split('?')[0];

    //Cropper runs this function with dimensions of image onload
    function handleMediaLoad(mediaSize: {
        width: number;
        height: number;
        naturalWidth: number;
        naturalHeight: number;
    }): void {
        setLoadedMediaSize(mediaSize);
        const { naturalWidth, naturalHeight } = mediaSize;
        //if the image rotated 90 doesn't meet size reqs, only allow rotation by 180
        const rotateBy =
            naturalWidth >= imageReqs.targetHeight && naturalHeight >= imageReqs.targetWidth
                ? 90
                : 180;
        setRotationFactor(rotateBy);
        //set zoom range such that rotated image zoomed in all the way meets min size reqs
        const zoomRange = getZoomRange(
            { width: naturalWidth, height: naturalHeight },
            imageReqs,
            rotateBy
        );
        setMaxZoom(zoomRange);
        if (parentImage !== imageUrl && initialCroppedAreaPixels?.width) {
            const croppedWidth = initialCroppedAreaPixels.width;
            const croppedHeight = initialCroppedAreaPixels.height;

            // image is zoomed if both dimensions are below natural
            if (croppedWidth < naturalWidth && croppedHeight < naturalHeight) {
                const zoomAlongLowerDim = Math.min(
                    naturalWidth / croppedWidth,
                    naturalHeight / croppedHeight
                );
                setZoom(zoomAlongLowerDim);
            }
        } else {
            setZoom(1);
        }
        //bug within Cropper causes NaN values if you replace image more than once
        //temp fix is to arbitrarily increase rotation to force Cropper to re-render
        setRotation(rotation + 360);
    }

    function onDeleteAndClose(): void {
        if (changesRequired) {
            onDelete();
        }
        onClose();
    }

    function onModalClose(): void {
        if (showConfirmationBeforeClose) {
            setExitAttempt(true);
        } else {
            onDeleteAndClose();
        }
    }

    return (
        <ModalContainer
            isOpen={isOpen}
            onClose={onClose}
            modalSize="large"
            closeOnOutsideClick={false}
            closeOnEsc={false}
        >
            {!exitAttempt ? (
                <>
                    <ModalCloseButton onClick={onModalClose} />
                    <ModalBody className={styles.modalBody}>
                        {showOnboardingCopy && changesRequired ? (
                            <Banner
                                message={
                                    <FormattedMessage
                                        id="imageEditModal.onboarding.changesRequired"
                                        defaultMessage="Your image must be cropped to fit within the required aspect ratio. Adjust the image by using drag or zoom."
                                    />
                                }
                                bannerType="error"
                                bodyClassName={styles.alertMessageBody}
                            />
                        ) : null}
                        {showOnboardingCopy && !changesRequired ? (
                            <div className={styles.onboardingMessageBody}>
                                <FormattedMessage
                                    id="imageEditModal.onboarding.noChangesRequired"
                                    defaultMessage="Adjust the image by using drag or zoom."
                                />
                            </div>
                        ) : null}

                        {title ? <Header title={title} /> : null}
                        {subTitle ? <p className={styles.subTitle}>{subTitle}</p> : null}

                        <div className={classNames(styles.cropContainer, customStyles)}>
                            <Cropper
                                image={parentImage}
                                crop={crop}
                                zoom={zoom}
                                rotation={rotation}
                                aspect={imageReqs.aspect}
                                cropShape={imageReqs.cropShape}
                                onCropChange={({ x, y }) => {
                                    // On the first call we get x: Infinity and y: Infinity, resulting in a bad image positioning
                                    // https://1stdibs.atlassian.net/browse/SELLERTOOLS-9315
                                    if (isFinite(x) && isFinite(y)) {
                                        setCrop({ x, y });
                                    }
                                }}
                                onCropComplete={onCropComplete}
                                zoomWithScroll={false}
                                onMediaLoaded={handleMediaLoad}
                                initialCroppedAreaPixels={initialCroppedAreaPixels}
                            />
                        </div>
                        <div className={styles.buttonContainer}>
                            <div className={styles.controlContainer}>
                                {maxZoom > 1 && (
                                    <>
                                        <span className={styles.zoomControl}>
                                            <FormattedMessage
                                                id="dibs-media-uploader.ImageEditModal.zoom"
                                                defaultMessage="Zoom"
                                            />
                                        </span>
                                        <Slider
                                            currentPosition={zoom}
                                            onSlide={setZoom}
                                            minRange={1}
                                            maxRange={maxZoom}
                                        />
                                    </>
                                )}
                                {enableImageRotation && (
                                    <Link
                                        onClick={() => setRotation(rotation + rotationFactor)}
                                        className={styles.rotateLink}
                                    >
                                        <FormattedMessage
                                            id="dibs-media-uploader.ImageEditModal.rotate"
                                            defaultMessage="{icon} Rotate"
                                            values={{
                                                icon: (
                                                    <ArrowClockwise className={styles.rotateIcon} />
                                                ),
                                            }}
                                        />
                                    </Link>
                                )}
                            </div>
                            <Button
                                dataTn="image-edit-save"
                                size="large"
                                onClick={onSaveAndClose}
                                disabled={
                                    initialCropArea === cropArea ||
                                    !cropArea.width ||
                                    !cropArea.height
                                }
                                className={styles.saveButton}
                            >
                                {saveCtaText ?? (
                                    <FormattedMessage
                                        id="imageEditModal.updateAndCloseCta"
                                        defaultMessage="Update & Close"
                                    />
                                )}
                            </Button>
                        </div>
                    </ModalBody>
                    <ModalSpinner isOpen={isSaving} />
                </>
            ) : (
                <>
                    <ModalCloseButton onClick={() => onDeleteAndClose()} />
                    <ModalBody className={styles.exitModalBody}>
                        <div className={styles.flexContainer}>
                            <ExclamationCircle className={styles.warningIcon} />
                            <Header
                                title={
                                    changesRequired ? (
                                        <FormattedMessage
                                            id="imageEditModal.confirmation..title.changesRequired"
                                            defaultMessage="Exit Image Upload"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="imageEditModal.confirmation..title.noChangesRequired"
                                            defaultMessage="Exit Image Editing"
                                        />
                                    )
                                }
                            />
                            <div className={styles.subtext}>
                                {changesRequired ? (
                                    <FormattedMessage
                                        id="imageEditModal.confirmation.changesRequired"
                                        defaultMessage="If you exit without editing, the image will not be uploaded because it does not meet the technical requirements."
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="imageEditModal.confirmation.noChangesRequired"
                                        defaultMessage="If you exit image editing all updates to the image will be lost."
                                    />
                                )}
                            </div>
                            <Button
                                dataTn="image-edit-close"
                                size="medium"
                                onClick={() => onDeleteAndClose()}
                                fullWidth
                            >
                                <FormattedMessage
                                    id="imageEditModal.confirmation.close"
                                    defaultMessage="Close"
                                />
                            </Button>
                            <Button
                                dataTn="image-edit-back"
                                size="medium"
                                onClick={() => setExitAttempt(false)}
                                type="secondary"
                                fullWidth
                            >
                                <FormattedMessage
                                    id="imageEditModal.confirmation.backToEditing"
                                    defaultMessage="Back to Image Editing"
                                />
                            </Button>
                        </div>
                    </ModalBody>
                </>
            )}
        </ModalContainer>
    );
};
